import { ContextMenu, Gantt, Toolbar } from "wx-react-gantt";
import "wx-react-gantt/dist/gantt.css";
import React, {
  useMemo,
  useCallback,
  useRef,
  useState,
  useEffect,
} from "react";
import { Willow } from "wx-react-gantt";
import "./myGantt.css";
import { BASE_URL } from "utils/constant";
import { taskApi } from "api/tasks/taskApi";
import { duration } from "moment";
import { formatDate } from "date-fns";

const TaskStatus = ({ status, type }) => {
  const getStatusStyle = (status) => {
    switch (status?.toLowerCase()) {
      case "complete":
        return "bg-green-100 text-green-800 border-green-200";
      case "ongoing":
        return "bg-blue-100 text-blue-800 border-blue-200";
      case "not started":
        return "bg-gray-100 text-gray-800 border-gray-200";
      default:
        return "bg-gray-100 text-gray-600 border-gray-200";
    }
  };

  return (
    <div className="flex items-center justify-center">
      <span
        className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusStyle(
          status
        )}`}
      >
        {status}
      </span>
      {type === "task" && (
        <span className="ml-1 text-xs text-gray-500">📋</span>
      )}
      {type === "subtask" && (
        <span className="ml-1 text-xs text-gray-400">📝</span>
      )}
    </div>
  );
};

const NewTimelineView = ({ taskGroups }) => {
  const apiRef = useRef(null);
  const [tasks, setTasks] = useState([]);
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (apiRef.current) {
      // Handle adding a new task (group, task, or subtask)
      apiRef.current.intercept("add-task", async (event) => {
        const { target, mode, task } = event;
        let newTaskData;
        try {
          if (mode === "child") {
            // Adding as a child of the target
            if (String(target).startsWith("group_")) {
              // **New Task** under a Task Group
              const groupId = Number(String(target).split("_")[1]);
              const payload = {
                site_id: Number(localStorage.getItem("site")), // example site context
                task_group: groupId,
                task_name: "New Task",
                task_details: "",
                task_status: "Not Started",
                start_date: formatDate(new Date()), // helper to format Date -> "YYYY-MM-DD"
                end_date: formatDate(new Date()),
                priority: 2,
                estimated_amount: "0",
              };
              newTaskData = await taskApi.addTask(payload); // call create-task API
              // newTaskData should contain the created task info including its `id`
              // Construct a Gantt task object for the new task:
              const ganttTask = {
                id: `task_${newTaskData.data.id}`,
                text: newTaskData.data.task_name,
                start: new Date(newTaskData.data.start_date),
                end: new Date(newTaskData.data.end_date),
                duration: 1, // or calculate days difference
                progress: 0,
                parent: `group_${groupId}`,
                type: "task",
                details: {
                  type: "task",
                  originalId: newTaskData.data.id,
                  status: newTaskData.data.task_status,
                  priority: newTaskData.data.priority,
                  allocated_members: newTaskData.data.allocated_members,
                  // ...other fields as needed
                },
              };
              // Update state: add new task and link from group->task
              setTasks((prev) => [...prev, ganttTask]);
              setLinks((prev) => [
                ...prev,
                {
                  id: `link_group_${groupId}_task_${newTaskData.data.id}`,
                  source: `group_${groupId}`,
                  target: `task_${newTaskData.data.id}`,
                  type: "s2s",
                },
              ]);
            } else if (String(target).startsWith("task_")) {
              // **New Subtask** under a Task
              const taskId = Number(String(target).split("_")[1]);
              const payload = {
                task_id: taskId,
                subtask_name: "New Subtask",
                subtask_details: "",
                end_date: formatDate(new Date()),
                priority: 2,
                assign_to: [],
              };
              const newSub = await taskApi.addSubtask(payload);
              const subId = newSub.id;
              const ganttTask = {
                id: `subtask_${subId}`,
                text: newSub.subtask_name,
                start: new Date(newSub.start_date),
                end: new Date(newSub.end_date),
                duration: 1,
                progress: newSub.is_completed ? 100 : 0,
                parent: `task_${taskId}`,
                type: "task",
                details: {
                  type: "subtask",
                  originalId: subId,
                  status: newSub.is_completed ? "Complete" : "Ongoing",
                  // ...other fields
                },
              };
              setTasks((prev) => [...prev, ganttTask]);
              setLinks((prev) => [
                ...prev,
                {
                  id: `link_task_${taskId}_subtask_${subId}`,
                  source: `task_${taskId}`,
                  target: `subtask_${subId}`,
                  type: "s2s",
                },
              ]);
            }
          } else if (mode === "before" || mode === "after") {
            // Adding a sibling task or a new group
            const targetId = String(target);
            if (!targetId.includes("_")) {
              return; // Invalid target format
            }
            const [targetType, targetNum] = targetId.split("_");
            if (targetType === "group") {
              // Adding a new Task Group at root level (before/after an existing group)
              const payload = {
                site_id: Number(localStorage.getItem("site")),
                task_group_name: "New Group",
                status: "Active",
                start_date: formatDate(new Date()),
                end_date: formatDate(new Date()),
                priority: 3,
              };
              const newGroup = await taskApi.addTaskGroup(payload);
              const groupId = newGroup.data.id;
              const ganttTask = {
                id: `group_${groupId}`,
                text: newGroup.data.group_name,
                start: new Date(newGroup.data.start_date),
                end: new Date(newGroup.data.end_date),
                duration: 1,
                progress: 0,
                type: "summary",
                details: {
                  type: "taskGroup",
                  originalId: groupId,
                  status: newGroup.data.status,
                  priority: newGroup.data.priority,
                  allocated_members: newGroup.data.allocated_members,
                },
              };
              // Insert new group in state at appropriate position (before/after target group)
              setTasks((prev) => {
                const index = prev.findIndex((t) => t.id === targetId);
                if (index === -1) return [...prev, ganttTask];
                const newTasks = [...prev];
                const insertIndex = mode === "before" ? index : index + 1;
                newTasks.splice(insertIndex, 0, ganttTask);
                return newTasks;
              });
              // No parent link needed for root group
            } else if (targetType === "task") {
              // Adding a new Task as sibling in the same group
              const targetTask = tasks.find((t) => t.id === targetId);
              if (!targetTask) return;
              const parentGroupId = targetTask.parent; // e.g. "group_5"
              const groupNum = parentGroupId
                ? Number(String(parentGroupId).split("_")[1])
                : null;
              if (!groupNum) {
                console.warn("Target task has no group parent");
                return;
              }
              // Similar to adding under group, but we'll insert at correct position:
              const payload = {
                task_group: groupNum,
                task_name: "New Task",
                task_details: "",
                task_status: "Not Started",
                start_date: formatDate(new Date()),
                end_date: formatDate(new Date()),
                priority: 2,
                estimated_amount: "0",
              };
              newTaskData = await taskApi.addTask(payload);
              const newTask = newTaskData.data;
              const ganttTask = {
                id: `task_${newTask.id}`,
                text: newTask.task_name,
                start: new Date(newTask.start_date),
                end: new Date(newTask.end_date),
                duration: 1,
                progress: 0,
                parent: parentGroupId,
                type: "task",
                details: {
                  type: "task",
                  originalId: newTask.id,
                  status: newTask.task_status,
                  priority: newTask.priority,
                  allocated_members: newTask.allocated_members,
                },
              };
              setTasks((prev) => {
                // insert new task next to target within the same group
                const newTasks = [...prev];
                const insertIndex = prev.findIndex((t) => t.id === targetId);
                newTasks.splice(
                  mode === "before" ? insertIndex : insertIndex + 1,
                  0,
                  ganttTask
                );
                return newTasks;
              });
              setLinks((prev) => [
                ...prev,
                {
                  id: `link_group_${groupNum}_task_${newTask.id}`,
                  source: `group_${groupNum}`,
                  target: `task_${newTask.id}`,
                  type: "s2s",
                },
              ]);
            } else if (targetType === "subtask") {
              // Adding a new Subtask as sibling under the same parent task
              const targetSub = tasks.find((t) => t.id === targetId);
              if (!targetSub) return;
              const parentTaskId = targetSub.parent; // e.g. "task_141"
              if (!parentTaskId) return;
              const taskNum = Number(String(parentTaskId).split("_")[1]);
              const payload = {
                task_id: taskNum,
                subtask_name: "New Subtask",
                subtask_details: "",
                end_date: formatDate(new Date()),
                priority: 2,
                assign_to: [],
              };
              const newSub = await taskApi.addSubtask(payload);
              const subId = newSub.id;
              const ganttTask = {
                id: `subtask_${subId}`,
                text: newSub.subtask_name,
                start: new Date(newSub.start_date),
                end: new Date(newSub.end_date),
                duration: 1,
                progress: newSub.is_completed ? 100 : 0,
                parent: parentTaskId,
                type: "task",
                details: {
                  type: "subtask",
                  originalId: subId,
                  status: newSub.is_completed ? "Complete" : "Ongoing",
                },
              };
              setTasks((prev) => {
                const newTasks = [...prev];
                const insertIndex = prev.findIndex((t) => t.id === targetId);
                newTasks.splice(
                  mode === "before" ? insertIndex : insertIndex + 1,
                  0,
                  ganttTask
                );
                return newTasks;
              });
              setLinks((prev) => [
                ...prev,
                {
                  id: `link_task_${taskNum}_subtask_${subId}`,
                  source: `task_${taskNum}`,
                  target: `subtask_${subId}`,
                  type: "s2s",
                },
              ]);
            }
          }
        } catch (err) {
          console.error("Failed to add task:", err);
          // Optionally, you could return false here to cancel the UI addition if error
        }
      });

      // Handle moving/resizing tasks (date changes)
      apiRef.current.on("update-task", async (event) => {
        const { id, task, inProgress } = event;
        if (inProgress) {
          // User is still dragging/resizing, skip API call until done
          return;
        }
        // Determine the type of item (group/task/subtask) by ID prefix
        try {
          if (String(id).startsWith("group_")) {
            const groupId = Number(String(id).split("_")[1]);
            // Prepare updates for Task Group (e.g., new start_date and end_date)
            const updates = {
              start_date: formatDate(task.start), // convert Date to "YYYY-MM-DD"
              end_date: formatDate(task.end),
            };
            await taskApi.updateTaskGroup(groupId, updates);
          } else if (String(id).startsWith("task_")) {
            const taskId = Number(String(id).split("_")[1]);
            const updates = {
              start_date: formatDate(task.start),
              end_date: formatDate(task.end),
            };
            await taskApi.updateTask(taskId, updates);
          } else if (String(id).startsWith("subtask_")) {
            const subId = Number(String(id).split("_")[1]);
            const updates = {
              start_date: formatDate(task.start),
              end_date: formatDate(task.end),
            };
            await taskApi.updateSubtask(subId, updates);
          }
          // Optionally, you might update local state here if your state isn't directly bound to task dates.
          console.log(`Updated ${id} dates on server`);
        } catch (err) {
          console.error("Failed to update task dates:", err);
          // You might revert the date change in state if the server call fails
        }
      });

      // Handle task deletion
      apiRef.current.on("delete-task", async (event) => {
        console.log("Delete-task event data:", event);
        // ... call API to delete task ...
      });
    }
  }, [apiRef]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const taskGroups = await taskApi.getAllTaskGroups();
      // console.log("Task Groups:", taskGroups);

      const ganttTasks = [];
      const ganttLinks = [];

      await Promise.all(
        taskGroups?.map(async (group) => {
          // Add task group
          ganttTasks.push({
            id: `group_${group.id}`,
            text: group.group_name,
            start: new Date(group.start_date),
            end: new Date(group.end_date),
            duration: duration(
              new Date(group.end_date) - new Date(group.start_date)
            ).asDays(),
            progress: calculateGroupProgress(group.task_group_summary),
            type: "summary",
            // status: group.status,
            details: {
              type: "taskGroup",
              originalId: group.id,
              status: group.status,
              priority: group.priority,
              allocated_members: group.allocated_members,
            },
          });

          try {
            const Tasks = await taskApi.getAllTasks(group.id);
            // console.log("Tasks for group:", group.id, Tasks);
            await Promise.all(
              Tasks?.map(async (task) => {
                // Add main task
                ganttTasks.push({
                  id: `task_${task.id}`,
                  text: task.task_name,
                  start: new Date(task.start_date),
                  end: new Date(task.end_date),
                  duration: duration(
                    new Date(task.end_date) - new Date(task.start_date)
                  ).asDays(),
                  progress: getTaskProgress(task.task_status),
                  parent: `group_${group.id}`,
                  type: "task",
                  // status: task.task_status,
                  details: {
                    type: "task",
                    originalId: task.id,
                    status: task.task_status,
                    priority: task.priority,
                    task_details: task.task_details,
                    estimated_amount: task.estimated_amount,
                    allocated_members: task.allocated_members,
                    task_group: task.task_group,
                  },
                });

                try {
                  const subtasks = await taskApi.getAllSubtasks(task.id);
                  // console.log("Subtasks:", subtasks);
                  subtasks?.map((subtask) => {
                    ganttTasks.push({
                      id: `subtask_${subtask.id}`,
                      text: subtask.subtask_name,
                      start: new Date(subtask.start_date),
                      end: new Date(subtask.end_date),
                      duration: duration(
                        new Date(subtask.end_date) -
                          new Date(subtask.start_date)
                      ).asDays(),
                      progress: subtask.is_completed ? 100 : 0,
                      parent: `task_${task.id}`,
                      // status: subtask.is_completed ? "Complete" : "Ongoing",
                      type: "task",
                      details: {
                        type: "subtask",
                        status: subtask.is_completed ? "Complete" : "Ongoing",
                        originalId: subtask.id,
                        is_completed: subtask.is_completed,
                        subtask_details: subtask.subtask_details,
                        task_id: subtask.task_id,
                        assign_to: subtask.assign_to,
                      },
                    });

                    ganttLinks.push({
                      id: `link_task_${task.id}_subtask_${subtask.id}`,
                      source: `task_${task.id}`,
                      target: `subtask_${subtask.id}`,
                      type: "s2s",
                    });
                  });
                } catch (err) {
                  console.warn(
                    `Failed to load subtasks for task ${task.id}:`,
                    err
                  );
                }

                ganttLinks.push({
                  id: `link_group_${group.id}_task_${task.id}`,
                  source: `group_${group.id}`,
                  target: `task_${task.id}`,
                  type: "s2s",
                });
              })
            );
          } catch (err) {
            console.warn(`Failed to load tasks for group ${group.id}:`, err);
          }
        })
      );

      setTasks(ganttTasks);
      setLinks(ganttLinks);
    } catch (err) {
      console.error("Error loading data:", err);
      setError("Failed to load data. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // DOM manipulation for custom status cells
  useEffect(() => {
    const customizeStatusCells = () => {
      // Target cells with data-row-id starting with 'task_' or 'subtask_' and data-col-id='status'
      const statusCells = document.querySelectorAll(
        '.wx-cell[data-row-id^="task_"][data-col-id="status"], ' +
          '.wx-cell[data-row-id^="subtask_"][data-col-id="status"]'
      );

      statusCells.forEach((cell) => {
        const rowId = cell.getAttribute("data-row-id");

        const status = tasks.find((task) => task.id === rowId)?.details?.status;

        // Skip if already customized
        if (cell.querySelector(".custom-status")) return;

        const getStatusClass = (status) => {
          switch (status?.toLowerCase()) {
            case "complete":
              return "bg-[#00C875] text-white";
            case "ongoing":
              return "bg-[#F0BA30] text-white";
            case "not started":
              return "bg-[#A4A6A7] text-white";
            case "overdue":
              return "bg-[#D30606] text-white";
            default:
              return "bg-gray-100 text-gray-600 border border-gray-200";
          }
        };

        const getIcon = (rowId) => {
          if (rowId.startsWith("task_")) return "📋";
          if (rowId.startsWith("subtask_")) return "📝";
          return "";
        };

        // Create custom status element
        const customStatus = document.createElement("div");
        customStatus.className =
          "custom-status flex items-center justify-center h-full";
        customStatus.innerHTML = `
          <span class="px-2  rounded-full text-[13px] font-medium ${getStatusClass(
            status
          )}">
            ${status}
          </span>
        `;

        // Replace cell content
        cell.innerHTML = "";
        cell.appendChild(customStatus);

        // Add some styling to the cell itself
        cell.style.padding = "4px";
        cell.style.display = "flex";
        cell.style.height = "42px";
        cell.style.alignItems = "center";
        // cell.style.backgroundColor = "red";
        cell.style.justifyContent = "center";
      });
    };

    // Run customization after a short delay to ensure DOM is ready
    const timer = setTimeout(customizeStatusCells, 200);

    // Also run when tasks change
    if (tasks.length > 0) {
      const observer = new MutationObserver((mutations) => {
        let shouldUpdate = false;
        mutations.forEach((mutation) => {
          if (mutation.type === "childList" || mutation.type === "attributes") {
            shouldUpdate = true;
          }
        });
        if (shouldUpdate) {
          setTimeout(customizeStatusCells, 100);
        }
      });

      // Observe the gantt container for changes
      const ganttContainer = document.querySelector(".myGantt");
      if (ganttContainer) {
        observer.observe(ganttContainer, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ["data-row-id", "data-col-id"],
        });
      }

      return () => {
        clearTimeout(timer);
        observer.disconnect();
      };
    }

    return () => clearTimeout(timer);
  }, [tasks]);

  // Helper functions
  const calculateGroupProgress = (summary) => {
    if (!summary || summary.total_tasks === 0) return 0;
    return Math.round((summary.completed_tasks / summary.total_tasks) * 100);
  };

  const getTaskProgress = (status) => {
    switch (status) {
      case "Complete":
        return 100;
      case "Ongoing":
        return 50;
      case "Not Started":
      default:
        return 0;
    }
  };

  const scales = [
    { unit: "month", step: 1, format: "MMMM yyy" },
    { unit: "day", step: 1, format: "d" },
  ];

  // Custom render function for status column
  const renderStatusCell = (task) => {
    // Return default for all - we'll customize via DOM
    return task.status || "";
  };

  const columns = [
    { id: "text", header: "Task Groups", flexgrow: 2, align: "left" },
    {
      id: "status",
      header: "Status",
      flexgrow: 1,
      align: "center",
    },
    {
      id: "end",
      header: "Due Date",
      flexgrow: 1,
      align: "center",
    },
    {
      id: "action",
      header: "",
      width: 50,
      align: "center",
    },
  ];

  const scaleHeightRef = useRef(40);
  const cellHeight = useRef(45);

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-red-500">{error}</div>
      </div>
    );
  }

  return (
    <div
      className="h-full p-2"
      style={{
        scrollbarWidth: "none",
      }}
    >
      <Willow>
        <div className="myGantt">
          <Gantt
            // ref={apiRef}
            api={apiRef}
            tasks={tasks}
            scaleHeight={scaleHeightRef.current}
            zoom={true}
            cellHeight={cellHeight.current}
            links={links}
            scales={scales}
            columns={columns}
          />
        </div>
      </Willow>
    </div>
  );
};

export default NewTimelineView;
